import React, { useEffect, useMemo, useState } from 'react';
import { normalizeAssetUrl } from '../../utils/vehicleImage';

const normalizePhotoCandidate = candidate => {
    if (typeof candidate !== 'string') {
        return '';
    }

    const trimmedCandidate = candidate.trim();

    if (!trimmedCandidate) {
        return '';
    }

    if (trimmedCandidate.startsWith('data:') || trimmedCandidate.startsWith('blob:')) {
        return trimmedCandidate;
    }

    const isLocalStaticAsset = /^(?:\.?\/)?(?:static|assets)\//i.test(trimmedCandidate);
    if (trimmedCandidate.startsWith('/static/') || trimmedCandidate.startsWith('/assets/') || isLocalStaticAsset) {
        const normalizedPath = trimmedCandidate.startsWith('/')
            ? trimmedCandidate
            : `/${trimmedCandidate.replace(/^\.?\//, '')}`;

        if (typeof window !== 'undefined' && window.location && window.location.origin) {
            return `${window.location.origin}${normalizedPath}`;
        }

        return normalizedPath;
    }

    return normalizeAssetUrl(candidate);
};

const normalizeAssetCandidate = candidate => {
    return normalizeAssetUrl(candidate);
};

const DriverBadgeCard = ({
    name,
    phone,
    vehicleInfo,
    permit,
    photoCandidates,
    fallbackPhoto,
    vehicleImageUrl,
    vehicleImageCandidates,
    width = 260,
    padding = '18px 18px 16px',
    borderRadius = 24,
    photoSize = 112,
    vehicleImageMaxHeight = 88,
    vehicleImageMinHeight = 94,
    vehicleImageScale = 1,
    titleFontSize = 28,
    phoneFontSize = 17,
    phoneIconSize = 28,
    phoneIconInnerSize = 13,
    vehicleInfoFontSize = 18,
    permitFontSize = 12,
}) => {
    const normalizedCandidates = useMemo(() => {
        const candidates = Array.isArray(photoCandidates) ? photoCandidates : [];
        const dedupedCandidates = candidates.reduce((accumulator, candidate) => {
            const normalizedCandidate = normalizePhotoCandidate(candidate);

            if (normalizedCandidate && !accumulator.includes(normalizedCandidate)) {
                accumulator.push(normalizedCandidate);
            }

            return accumulator;
        }, []);

        const normalizedFallbackPhoto = normalizePhotoCandidate(fallbackPhoto);

        if (normalizedFallbackPhoto && !dedupedCandidates.includes(normalizedFallbackPhoto)) {
            dedupedCandidates.push(normalizedFallbackPhoto);
        }

        return dedupedCandidates;
    }, [fallbackPhoto, photoCandidates]);
    const normalizedVehicleCandidates = useMemo(() => {
        const candidates = Array.isArray(vehicleImageCandidates) ? vehicleImageCandidates : [];
        const dedupedCandidates = candidates.reduce((accumulator, candidate) => {
            const normalizedCandidate = normalizeAssetCandidate(candidate);

            if (normalizedCandidate && !accumulator.includes(normalizedCandidate)) {
                accumulator.push(normalizedCandidate);
            }

            return accumulator;
        }, []);

        const normalizedVehicleImageUrl = normalizeAssetCandidate(vehicleImageUrl);

        if (normalizedVehicleImageUrl && !dedupedCandidates.includes(normalizedVehicleImageUrl)) {
            dedupedCandidates.unshift(normalizedVehicleImageUrl);
        }

        return dedupedCandidates;
    }, [vehicleImageCandidates, vehicleImageUrl]);
    const [photoCandidateIndex, setPhotoCandidateIndex] = useState(0);
    const [vehicleImageCandidateIndex, setVehicleImageCandidateIndex] = useState(0);
    const currentPhoto = normalizedCandidates[Math.min(photoCandidateIndex, Math.max(normalizedCandidates.length - 1, 0))] || '';
    const normalizedVehicleImageUrl = vehicleImageCandidateIndex >= 0
        ? (normalizedVehicleCandidates[Math.min(vehicleImageCandidateIndex, Math.max(normalizedVehicleCandidates.length - 1, 0))] || '')
        : '';

    useEffect(() => {
        setPhotoCandidateIndex(0);
    }, [normalizedCandidates]);

    useEffect(() => {
        setVehicleImageCandidateIndex(0);
    }, [normalizedVehicleCandidates]);

    return (
        <div
            style={{
                width,
                padding,
                background: '#ffffff',
                borderRadius,
                boxShadow: '0 22px 44px rgba(15, 23, 42, 0.24)',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                color: '#111827',
                textAlign: 'center',
            }}
        >
            <div style={{ fontSize: titleFontSize, lineHeight: 1.05, fontWeight: 800, marginBottom: phone ? 10 : 16 }}>
                {name || 'Driver'}
            </div>
            {phone ? (
                <div
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10,
                        fontSize: phoneFontSize,
                        fontWeight: 600,
                        marginBottom: 16,
                    }}
                >
                    <span
                        style={{
                            width: phoneIconSize,
                            height: phoneIconSize,
                            borderRadius: '50%',
                            background: '#111827',
                            color: '#ffffff',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <i className="fas fa-phone-alt" aria-hidden="true" style={{ fontSize: phoneIconInnerSize }} />
                    </span>
                    <span>{phone}</span>
                </div>
            ) : null}
            <div
                style={{
                    width: photoSize,
                    height: photoSize,
                    margin: '0 auto 18px',
                    borderRadius: '18px',
                    overflow: 'hidden',
                    background: 'linear-gradient(180deg, #eef2f7 0%, #dbe4ee 100%)',
                    border: '1px solid rgba(15, 23, 42, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {currentPhoto ? (
                    <img
                        src={currentPhoto}
                        alt={name || 'Driver profile'}
                        referrerPolicy="no-referrer"
                        onError={() => {
                            setPhotoCandidateIndex(currentIndex => {
                                if (currentIndex >= normalizedCandidates.length - 1) {
                                    return -1;
                                }

                                return currentIndex + 1;
                            });
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                ) : (
                    <i className="fas fa-user" aria-hidden="true" style={{ fontSize: photoSize * 0.42, color: '#6b7280' }} />
                )}
            </div>
            {vehicleInfo ? (
                <div style={{ fontSize: vehicleInfoFontSize, lineHeight: 1.2, fontWeight: 700, marginBottom: 12 }}>
                    {vehicleInfo}
                </div>
            ) : null}
            <div
                style={{
                    minHeight: vehicleImageMinHeight,
                    borderRadius: '18px',
                    background: 'linear-gradient(180deg, #f6f8fb 0%, #e8eef6 100%)',
                    border: '1px solid rgba(148, 163, 184, 0.26)',
                    padding: normalizedVehicleImageUrl ? '10px 12px' : '14px 12px 6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                {normalizedVehicleImageUrl ? (
                    <img
                        src={normalizedVehicleImageUrl}
                        alt={vehicleInfo || 'Vehicle'}
                        referrerPolicy="no-referrer"
                        onError={() => {
                            setVehicleImageCandidateIndex(currentIndex => {
                                if (currentIndex >= normalizedVehicleCandidates.length - 1) {
                                    return -1;
                                }

                                return currentIndex + 1;
                            });
                        }}
                        style={{
                            width: '100%',
                            maxHeight: vehicleImageMaxHeight,
                            display: 'block',
                            objectFit: 'contain',
                            transform: `scale(${vehicleImageScale})`,
                            transformOrigin: 'center center'
                        }}
                    />
                ) : (
                    <div
                        style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: '#111827',
                            opacity: 0.8,
                        }}
                        aria-label="Vehicle marker fallback"
                        title="Vehicle image unavailable"
                    >
                        {' '}
                    </div>
                )}
            </div>
            {permit ? (
                <div style={{ marginTop: 10, fontSize: permitFontSize, color: '#6b7280', fontWeight: 700 }}>
                    Permit: {permit}
                </div>
            ) : null}
        </div>
    );
};

export default DriverBadgeCard;